import React, { useState, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

type PieItem = {
  name: string;
  value: number;
  color: string;
};

type Props = {
  title: string;
  data: PieItem[];
};

const AnalyticsPieCard: React.FC<Props> = ({ title, data }) => {
  // State untuk menyimpan item mana yang sedang di-filter (aktif)
  // Null berarti semua tampil, jika ada string berarti hanya kategori itu
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Filter data berdasarkan state
  const filteredData = useMemo(() => {
    if (!activeFilter) return data;
    return data.filter((item) => item.name === activeFilter);
  }, [activeFilter, data]);

  const handleLegendClick = (name: string) => {
    // Jika klik pada yang sudah aktif, reset (tampilkan semua)
    // Jika tidak, tampilkan hanya yang diklik
    setActiveFilter(activeFilter === name ? null : name);
  };

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="d-flex flex-column align-items-center w-100 p-4 border rounded shadow-sm" style={{ minHeight: '350px' }}>
      
      {/* 1. CHART CONTAINER */}
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={filteredData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={2}
              // Animasi transisi saat data berubah
              animationDuration={500}
            >
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* 2. INTERACTIVE LEGEND */}
      <div className="d-flex flex-wrap justify-content-center gap-3 mt-4 w-100 px-2">
        {data.map((entry, index) => {
          const isDimmed = activeFilter && activeFilter !== entry.name;
          const percent = (((entry.value / totalValue) * 100) || 0).toFixed(0);

          return (
            <div 
              key={`legend-${index}`} 
              onClick={() => handleLegendClick(entry.name)}
              className="d-flex align-items-center gap-2"
              style={{ 
                cursor: 'pointer', 
                opacity: isDimmed ? 0.3 : 1, // Beri efek redup jika tidak dipilih
                transition: 'opacity 0.3s ease',
                userSelect: 'none'
              }}
            >
              <div 
                style={{ 
                  width: '12px', height: '12px', 
                  backgroundColor: entry.color, borderRadius: '50%' 
                }} 
              />
              <span className={`fs-8 ${activeFilter === entry.name ? 'fw-bold text-primary' : 'fw-semibold text-gray-700'}`}>
                {entry.name} <span className="text-gray-500 fw-normal">({percent}%)</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* 3. FOOTER */}
      <div className="mt-auto pt-3 text-center w-100 border-top mt-3">
        <span className="card-label fw-bold text-gray-800 fs-7">
            {activeFilter ? `Menampilkan: ${activeFilter}` : title}
        </span>
        {activeFilter && (
            <div 
                className="text-primary fs-9 cursor-pointer mt-1" 
                onClick={() => setActiveFilter(null)}
            >
                Tampilkan Semua
            </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPieCard;